import { normalizeContentRegion } from '../feed/region.ts';
import { invidiousLocaleTagFor } from '../i18n/invidiousLocales.ts';
import { normalizeUiLocale } from '../i18n/locale.ts';
import { PLAYBACK_RATES, clampPlaybackRate } from '../playback/select.ts';
import type { InstancePreferences, PreferencesPatch } from '../auth/preferences.ts';
import type { LandingId, ThemeChoice, VisiousSettings } from './model.ts';

/**
 * §8's preference-sync table, as a function each way — and it corrects two of its eight rows.
 *
 * The rule (revised 2026-08-01, replacing the sign-in prompt): **the instance is the source of
 * truth for every setting it has a field for, and the local stores are its cache.** Signed in, a
 * boot pulls the account's preferences and applies them over the cache; a local change pushes back.
 * Signed out, the cache is simply the settings. There is still no merge function anywhere — a pull
 * applies the account's values wholesale (minus the one rule below), and {@link canPushSettings}
 * refuses a push until a pull has landed, because a push carries all ten mapped fields and
 * pushing from a stale cache would overwrite the account with values it already replaced.
 *
 * ### The one rule that makes both directions safe
 *
 * **An instance value this build cannot express leaves the local setting alone.** Not a default, not a
 * guess — untouched. It applies to a `quality_dash` of `best`, a `default_home` of `""` (Invidious's
 * "Search" home, which Visious has no equivalent of), a `quality` of `medium` (progressive, which §2
 * forbids), and to any of the values finding 4 lets the endpoint store — `region: "ZZ"`,
 * `dark_mode: "maybe"`, `quality: "totally-bogus"`. Without it, a pull would quietly reset settings on
 * behalf of a field it did not understand.
 *
 * ### Two rows of §8's table are wrong, and one field is missing from it
 *
 * - **`local` → "proxy-media toggle" cannot exist.** §2 makes `?local=true` mandatory — a direct
 *   googlevideo URL is refused from a browser — so the toggle would have an off position that breaks
 *   playback. Not synced (`PREFERENCE_FIELDS_WITHOUT_A_SETTING`).
 * - **`captions[]` → "`SubtitlePref` language order" cannot be mapped.** The field stores English
 *   *display names*, not tags: harvested from the instance's own `<select name="captions[0]">`, its 131
 *   options are `English`, `English (auto-generated)`, `English (United Kingdom)`, `Afrikaans`, … with
 *   `value` equal to the label. `SubtitlePref` addresses a track by **language tag** (`de`), because
 *   that is what a caption track carries. The only place the two are paired is a *video's own*
 *   `captions[]` array (`label` + `languageCode` together), so a global table would be invented rather
 *   than measured. The subtitle language therefore stays local.
 * - **`region` is not in §8's table at all and it is the same setting** as `contentRegion` — which also
 *   explains where the `US` in the known gaps came from: it is the account's stored preference, not a
 *   Visious default.
 *
 * ### The value sets are the instance's own
 *
 * Harvested from `/preferences` on 2026-07-30, the way `CONTENT_REGIONS` was: `dark_mode` is
 * `"" | light | dark` (so `system` is the empty string, not the word), `quality` is
 * `dash | hd720 | medium | small`, `quality_dash` is `auto | best | 4320p … 144p | worst`, `speed` is
 * exactly {@link PLAYBACK_RATES}, and `default_home` is `"" | Popular | Trending` **unauthenticated** —
 * a signed-in account also offers `Subscriptions` and `Playlists`, which is where the stored
 * `feed_menu` value comes from.
 */

/** The settings that have an Invidious field. The page shows this list rather than implying more. */
export type SyncedSettingId =
  | 'theme'
  | 'uiLocale'
  | 'contentRegion'
  | 'landing'
  | 'quality'
  | 'playbackRate'
  | 'volume'
  | 'autoplay'
  | 'mirrorWatchHistory'
  | 'resumePlayback';

export const SYNCED_SETTINGS: readonly SyncedSettingId[] = [
  'theme',
  'uiLocale',
  'contentRegion',
  'landing',
  'quality',
  'playbackRate',
  'volume',
  'autoplay',
  'mirrorWatchHistory',
  'resumePlayback',
];

/** The wire field each one is stored in, for the page's own honesty about what a push writes. */
export const SYNCED_SETTING_FIELDS: Readonly<Record<SyncedSettingId, string>> = {
  theme: 'dark_mode',
  /*
   * The one row of the ten that is **conditional in both directions**. It is pushed only
   * when the reader picked a language, and pulled only when the account's language is one this build
   * has a catalogue for — so an account reading Deutsch keeps reading Deutsch through every push
   * Visious makes, and its own preferences page keeps showing the language its reader chose.
   */
  uiLocale: 'locale',
  contentRegion: 'region',
  landing: 'default_home',
  quality: 'quality_dash',
  playbackRate: 'speed',
  volume: 'volume',
  autoplay: 'autoplay',
  mirrorWatchHistory: 'watch_history',
  /*
   * The switch syncs; the positions cannot. `save_player_pos` is a boolean and there is no field
   * anywhere in the 35 that could hold a playhead — so this row is honest only because the setting it
   * names is "remember where I stopped" rather than "where I stopped".
   */
  resumePlayback: 'save_player_pos',
};

/**
 * Settings that stay in this browser, with the reason — the counterpart of the list above.
 *
 * `muted` and `useStableVolume` have no field; `subtitle`/`audio` have none that can be mapped (see
 * the header). They are named here so the settings page can say "this one does not follow you" next to
 * the control rather than in a footnote nobody reads.
 */
export type LocalOnlySettingId = 'muted' | 'useStableVolume' | 'audioLanguage' | 'subtitleLanguage';

export const LOCAL_ONLY_SETTINGS: readonly LocalOnlySettingId[] = [
  'muted',
  'useStableVolume',
  'audioLanguage',
  'subtitleLanguage',
];

/** `quality_dash`'s own options. A pinned height outside this set is not pushed — see the header. */
export const INVIDIOUS_DASH_HEIGHTS: readonly number[] = [4320, 2160, 1440, 1080, 720, 480, 360, 240, 144];

const LANDING_TO_DEFAULT_HOME: Readonly<Record<LandingId, string | null>> = {
  popular: 'Popular',
  subscriptions: 'Subscriptions',
  playlists: 'Playlists',
  // Invidious has no history home. `null` means "do not push this field", which leaves whatever the
  // account has rather than writing a value its own UI cannot show.
  history: null,
};

const DEFAULT_HOME_TO_LANDING: Readonly<Record<string, LandingId>> = {
  Popular: 'popular',
  Subscriptions: 'subscriptions',
  Playlists: 'playlists',
  /*
   * The account may still say `Trending` — Invidious has that home and this app no longer has that
   * screen. It reads as Popular, which is where those rows moved to, so an account configured on
   * the instance's own UI opens somewhere sensible instead of falling through to the default by accident.
   * Nothing writes the word back: `LANDING_TO_DEFAULT_HOME` has no key that produces it.
   */
  Trending: 'popular',
};

const THEME_TO_DARK_MODE: Readonly<Record<ThemeChoice, string>> = {
  // The instance's own "auto" is the **empty string**, harvested from its `<select>`. Writing the word
  // would store a value verbatim (finding 4) that its UI then cannot show as selected.
  system: '',
  dark: 'dark',
  light: 'light',
};

function themeFromDarkMode(value: string | null): ThemeChoice | null {
  if (value === null) return null;
  if (value === '') return 'system';
  if (value === 'dark' || value === 'light') return value;
  // `auto` is accepted by the endpoint and stored verbatim, but it is not one of the three options the
  // instance offers, so it is read rather than written.
  if (value === 'auto') return 'system';
  return null;
}

/**
 * Local settings → the fields a `POST` may carry.
 *
 * A field is **omitted** rather than defaulted whenever the local value has no wire representation, so
 * the merge in `preferencesWriteBody` leaves the account's own value in place.
 */
export function preferencesPatchFromSettings(settings: VisiousSettings): PreferencesPatch {
  const quality = settings.playback.quality;
  const pinned = quality.kind === 'pinned' && INVIDIOUS_DASH_HEIGHTS.includes(quality.height);
  const defaultHome = LANDING_TO_DEFAULT_HOME[settings.landing];
  // `null` twice over, and they mean different things that happen to want the same omission: the
  // reader has not chosen a language (so the account's own is not ours to change), or they chose one
  // the instance has no option for (so writing it would empty its language dropdown, §8d finding 4).
  const locale = settings.uiLocale === null ? null : invidiousLocaleTagFor(settings.uiLocale);

  return {
    dark_mode: THEME_TO_DARK_MODE[settings.theme],
    ...(locale === null ? {} : { locale }),
    // Only when there is one: an empty `region` 500s the trending endpoint, and "no preference" is a
    // real state here (`feed/region.ts`) that the wire cannot hold.
    ...(settings.contentRegion === null ? {} : { region: settings.contentRegion }),
    ...(defaultHome === null ? {} : { default_home: defaultHome }),
    // §2: Visious plays the adaptive manifest, always. So the DASH ladder is what this setting *is*,
    // and `quality` says so; the height lives in `quality_dash`.
    quality: 'dash',
    ...(quality.kind === 'auto' ? { quality_dash: 'auto' } : {}),
    ...(pinned && quality.kind === 'pinned' ? { quality_dash: `${quality.height}p` } : {}),
    speed: clampPlaybackRate(settings.playback.playbackRate),
    // 0–1 here, 0–100 and **Int32** there. `Math.round` is not cosmetic: a fractional `volume`
    // discards the entire write with a `204` (finding 3).
    volume: Math.round(Math.min(1, Math.max(0, settings.volume)) * 100),
    autoplay: settings.autoplay,
    watch_history: settings.mirrorWatchHistory,
    save_player_pos: settings.resumePlayback,
  };
}

/** `1080p` → `1080`. `auto`, `best`, `worst` and anything else → `null` (leave the local pin alone). */
function heightFromDashQuality(value: string | null): number | 'auto' | null {
  if (value === null) return null;
  if (value === 'auto') return 'auto';
  const match = /^(\d{3,4})p$/.exec(value);
  if (match === null) return null;
  const height = Number(match[1]);
  return INVIDIOUS_DASH_HEIGHTS.includes(height) ? height : null;
}

/**
 * The instance's preferences → the settings a **pull** would produce, over the current ones.
 *
 * `base` is not a formality: it is what every unmappable field falls back to (see the header's one
 * rule), and it is why this returns a whole `VisiousSettings` rather than a partial — the caller stores
 * a document, and a partial would make "which fields did the pull actually decide?" a question at every
 * call site instead of here.
 */
export function settingsFromPreferences(
  prefs: InstancePreferences,
  base: VisiousSettings,
): VisiousSettings {
  const theme = themeFromDarkMode(prefs.darkMode);
  const region = normalizeContentRegion(prefs.region);
  const landing = prefs.defaultHome === null ? undefined : DEFAULT_HOME_TO_LANDING[prefs.defaultHome];
  const dash = heightFromDashQuality(prefs.qualityDash);
  const codec = base.playback.quality.codec;

  return {
    ...base,
    theme: theme ?? base.theme,
    /*
     * A language this build has no catalogue for leaves the local choice alone — the header's one rule,
     * and here it is doing more than usual. `normalizeUiLocale` answers `null` both for "the account has
     * no language set" and for "the account reads Deutsch", and neither may become `'en'`: the first
     * would invent a choice, and the second would let a reader who pulls their settings *lose* the
     * language their account is in, on a build that could not have shown it to them anyway.
     */
    uiLocale: normalizeUiLocale(prefs.locale) ?? base.uiLocale,
    /*
     * A `region` the trending endpoint would refuse leaves the local value alone rather than becoming
     * `null` — and this is the one place where "unmappable → untouched" is doing real work. The
     * endpoint answers **500 with a leaked `KeyError`** for 142 of the 249 ISO-3166 codes, and it will
     * happily *store* one (finding 4), so an account whose region is `ZZ` must not be able to switch
     * Trending off for a reader who pulls its settings.
     */
    contentRegion: prefs.region === null ? base.contentRegion : (region ?? base.contentRegion),
    landing: landing ?? base.landing,
    playback: {
      ...base.playback,
      quality:
        dash === null
          ? base.playback.quality
          : dash === 'auto'
            ? { kind: 'auto', ...(codec === undefined ? {} : { codec }) }
            : { kind: 'pinned', height: dash, ...(codec === undefined ? {} : { codec }) },
      /*
       * Tested against the ladder **without** snapping first, and a test caught the difference: the
       * endpoint stores any float (`speed: 99`), and `clampPlaybackRate(99)` is `2` — a perfectly
       * offered rate. Snapping before the check therefore turns every junk value into a setting
       * instead of leaving the local one alone, which is this file's one rule.
       */
      playbackRate:
        prefs.speed !== null && PLAYBACK_RATES.includes(prefs.speed)
          ? prefs.speed
          : base.playback.playbackRate,
    },
    // 0–100 → 0–1, and out-of-range values are stored (`volume: 500`, `volume: -5`), so clamp.
    volume:
      prefs.volume === null ? base.volume : Math.min(1, Math.max(0, prefs.volume / 100)),
    autoplay: prefs.autoplay ?? base.autoplay,
    mirrorWatchHistory: prefs.watchHistory ?? base.mirrorWatchHistory,
    resumePlayback: prefs.savePlayerPos ?? base.resumePlayback,
  };
}

/**
 * Where a browser stands with the account's preferences.
 *
 * `syncing` is the state the cache model needs a name for: there is a session, and the account's
 * values have not arrived yet this boot — either the pull is in flight or it failed and is waiting
 * to be retried. A push in that state would carry the stale cache over the account, which is the
 * overwrite the pull exists to prevent, so {@link canPushSettings} refuses it.
 */
export type SettingsSyncState = 'signed-out' | 'syncing' | 'synced';

export function settingsSyncState(input: {
  readonly signedIn: boolean;
  /** Whether this boot has successfully applied the account's preferences over the cache. */
  readonly pulled: boolean;
}): SettingsSyncState {
  if (!input.signedIn) return 'signed-out';
  return input.pulled ? 'synced' : 'syncing';
}

/** Whether a local change may be pushed. Only ever over values this boot has already pulled. */
export function canPushSettings(state: SettingsSyncState): boolean {
  return state === 'synced';
}
